import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Row,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import Select from "react-select";
import cogoToast from "cogo-toast";
import {
  FaBell,
  FaSync,
  FaSearch,
  FaUsers,
  FaCog,
  FaInbox,
  FaPlus,
  FaTrash,
  FaPlay,
} from "react-icons/fa";
import { genericGet, genericPost, getList } from "../../services/Api";
import { ENDPOINTS, SUCCESS_RESULT } from "../../Constants";

const normalizeArray = (data) => {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.recipients)) return data.recipients;
  if (Array.isArray(data.mailing_list)) return data.mailing_list;
  return [];
};

const formatCell = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatSettings = (settings) => {
  if (!settings || typeof settings !== "object") return [];
  return Object.entries(settings).map(([key, value]) => ({
    key,
    label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
    value: formatCell(value),
  }));
};

const formatRunResultItem = (result) => {
  if (!result || typeof result !== "object") return [];

  const labelMap = {
    tag: "Type",
    days_before: "Days before expiry",
    emails_sent: "Emails sent",
    sent_count: "Emails sent",
    recipients_notified: "Recipients notified",
    contracts_found: "Contracts found",
    total_sent: "Total sent",
    total_failed: "Failed",
    message: "Details",
    status: "Status",
  };
  const skipKeys = new Set(["system_notification_id"]);

  return Object.entries(result)
    .filter(([key, value]) => !skipKeys.has(key) && value !== null && value !== undefined && value !== "")
    .map(([key, value]) => {
      const label = labelMap[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      if (Array.isArray(value)) {
        return value.length > 0 ? `${label}: ${value.join(", ")}` : null;
      }
      if (typeof value === "object") return null;
      return `${label}: ${value}`;
    })
    .filter(Boolean);
};

const formatRunNotificationSummary = (apiData) => {
  const lines = [];
  const message = apiData?.message?.trim();
  if (message) lines.push(message);

  const payload = apiData?.data;
  if (!payload || typeof payload !== "object") {
    return lines.length > 0 ? lines : ["Notification run completed."];
  }

  if (payload.total_processed != null) {
    const count = Number(payload.total_processed);
    if (!Number.isNaN(count)) {
      lines.push(`${count} notification${count === 1 ? "" : "s"} processed.`);
    }
  }

  const results = Array.isArray(payload.results) ? payload.results : [];
  results.forEach((result, index) => {
    const detailLines = formatRunResultItem(result);
    detailLines.forEach((line) => {
      lines.push(results.length > 1 ? `Result ${index + 1} — ${line}` : line);
    });
  });

  return lines.length > 0 ? lines : ["Notification run completed."];
};

const getRecipientId = (recipient) =>
  recipient?.id || recipient?.mailing_list_id || recipient?.recipient_id || null;

const getRecipientDisplayName = (recipient) =>
  recipient?.display_name ||
  recipient?.name ||
  recipient?.user_name ||
  recipient?.full_name ||
  recipient?.staff_name ||
  recipient?.email ||
  "this recipient";

const SystemNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [listSearch, setListSearch] = useState("");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [showAddRecipientModal, setShowAddRecipientModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isAddingRecipient, setIsAddingRecipient] = useState(false);
  const [recipientToDelete, setRecipientToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingRecipient, setIsDeletingRecipient] = useState(false);
  const [isRunningNotification, setIsRunningNotification] = useState(false);
  const [runNotificationResponse, setRunNotificationResponse] = useState(null);

  const fetchNotifications = useCallback(async () => {
    setIsLoadingList(true);
    try {
      let res = await genericGet(ENDPOINTS.NOTIFICATIONS_LIST);
      if (!(res?.status === 200 && res?.data?.status === SUCCESS_RESULT)) {
        res = await genericPost(ENDPOINTS.NOTIFICATIONS_LIST, {});
      }
      if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
        setNotifications(normalizeArray(res.data.data));
      } else {
        cogoToast.error(res?.data?.message || "Failed to load notifications.");
        setNotifications([]);
      }
    } catch {
      cogoToast.error("Failed to load notification types.");
      setNotifications([]);
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  const fetchRecipients = useCallback((notification) => {
    const systemNotificationId = notification?.system_notification_id;
    if (!systemNotificationId) {
      setRecipients([]);
      return;
    }

    setIsLoadingRecipients(true);
    genericPost(ENDPOINTS.NOTIFICATIONS_MAILING_LIST, {
      system_notification_id: systemNotificationId,
    })
      .then((res) => {
        setIsLoadingRecipients(false);
        if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
          setRecipients(normalizeArray(res.data.data));
        } else {
          cogoToast.error(res?.data?.message || "Failed to load recipients.");
          setRecipients([]);
        }
      })
      .catch(() => {
        setIsLoadingRecipients(false);
        cogoToast.error("Failed to load recipients.");
        setRecipients([]);
      });
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const loadUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      let res = await genericGet(ENDPOINTS.LIST_ALL_USERS);
      if (!(res?.status === 200 && res?.data?.status === SUCCESS_RESULT)) {
        const list = await getList(ENDPOINTS.LIST_USERS);
        setUsers(Array.isArray(list) ? list : []);
        return;
      }
      setUsers(Array.isArray(res.data.data) ? res.data.data : []);
    } catch {
      try {
        const list = await getList(ENDPOINTS.LIST_USERS);
        setUsers(Array.isArray(list) ? list : []);
      } catch {
        cogoToast.error("Failed to load users.");
        setUsers([]);
      }
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        value: user.id ?? user.user_id ?? user.value,
        label: `${user.name || user.text || user.email || "User"} (${user.email || user.telephone || user.id})`,
        country_id: user.country_id ?? user.country ?? 2,
        user,
      })),
    [users]
  );

  const openAddRecipientModal = () => {
    if (!selectedNotification?.tag) {
      cogoToast.error("Please select a notification type first.");
      return;
    }
    setSelectedUser(null);
    setShowAddRecipientModal(true);
    if (users.length === 0) {
      loadUsers();
    }
  };

  const closeAddRecipientModal = () => {
    if (isAddingRecipient) return;
    setShowAddRecipientModal(false);
    setSelectedUser(null);
  };

  const handleAddRecipient = async (e) => {
    e?.preventDefault();
    if (!selectedNotification?.tag) {
      cogoToast.error("Notification tag is missing.");
      return;
    }
    if (!selectedUser?.value) {
      cogoToast.error("Please select a user.");
      return;
    }

    setIsAddingRecipient(true);
    try {
      const res = await genericPost(ENDPOINTS.NOTIFICATIONS_MAILING_LIST_ADD, {
        tag: selectedNotification.tag,
        recipient_type: "user",
        user_id: Number(selectedUser.value),
        country_id: 2,
      });
      if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
        cogoToast.success(res?.data?.message || "Recipient added successfully.");
        setShowAddRecipientModal(false);
        setSelectedUser(null);
        fetchRecipients(selectedNotification);
        fetchNotifications();
      } else {
        cogoToast.error(res?.data?.message || "Failed to add recipient.");
      }
    } catch {
      cogoToast.error("Failed to add recipient.");
    } finally {
      setIsAddingRecipient(false);
    }
  };

  const openDeleteRecipientModal = (recipient) => {
    const recipientId = getRecipientId(recipient);
    if (!recipientId) {
      cogoToast.error("Recipient ID is missing. Cannot remove this record.");
      return;
    }
    setRecipientToDelete(recipient);
    setShowDeleteModal(true);
  };

  const closeDeleteRecipientModal = () => {
    if (isDeletingRecipient) return;
    setShowDeleteModal(false);
    setRecipientToDelete(null);
  };

  const handleRemoveRecipient = async () => {
    const recipientId = getRecipientId(recipientToDelete);
    if (!recipientId) {
      cogoToast.error("Recipient ID is missing.");
      return;
    }

    setIsDeletingRecipient(true);
    try {
      const res = await genericPost(ENDPOINTS.NOTIFICATIONS_MAILING_LIST_REMOVE, {
        id: recipientId,
      });
      if (res?.status === 200 && res?.data?.status === SUCCESS_RESULT) {
        cogoToast.success(res?.data?.message || "Recipient removed successfully.");
        setShowDeleteModal(false);
        setRecipientToDelete(null);
        if (selectedNotification) {
          fetchRecipients(selectedNotification);
          fetchNotifications();
        }
      } else {
        cogoToast.error(res?.data?.message || "Failed to remove recipient.");
      }
    } catch {
      cogoToast.error("Failed to remove recipient.");
    } finally {
      setIsDeletingRecipient(false);
    }
  };

  const handleSelectNotification = (notification) => {
    setSelectedNotification(notification);
    setRecipientSearch("");
    setRunNotificationResponse(null);
    fetchRecipients(notification);
  };

  const handleRunNotification = async () => {
    const systemNotificationId = selectedNotification?.system_notification_id;
    if (!systemNotificationId) {
      cogoToast.error("System notification ID is missing.");
      return;
    }

    setIsRunningNotification(true);
    setRunNotificationResponse(null);
    try {
      const res = await genericPost(ENDPOINTS.NOTIFICATIONS_RUN, {
        system_notification_id: systemNotificationId,
      });
      const isSuccess = res?.status === 200 && res?.data?.status === SUCCESS_RESULT;
      setRunNotificationResponse({
        success: isSuccess,
        summaryLines: formatRunNotificationSummary(res?.data),
      });
      if (isSuccess) {
        cogoToast.success(res?.data?.message || "Notification run completed.");
      } else {
        cogoToast.error(res?.data?.message || "Failed to run notification.");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to run notification.";
      setRunNotificationResponse({
        success: false,
        summaryLines: [errorMessage],
      });
      cogoToast.error(errorMessage);
    } finally {
      setIsRunningNotification(false);
    }
  };

  const filteredNotifications = useMemo(() => {
    const query = listSearch.trim().toLowerCase();
    if (!query) return notifications;
    return notifications.filter((item) => {
      const haystack = [
        item?.name,
        item?.tag,
        item?.module,
        item?.system_notification_id,
        JSON.stringify(item?.settings || {}),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [notifications, listSearch]);

  const filteredRecipients = useMemo(() => {
    const query = recipientSearch.trim().toLowerCase();
    if (!query) return recipients;
    return recipients.filter((item) =>
      JSON.stringify(item || {})
        .toLowerCase()
        .includes(query)
    );
  }, [recipients, recipientSearch]);

  const settingsItems = formatSettings(selectedNotification?.settings);

  return (
    <div className="container-fluid py-3">
      <div
        className="mb-3 p-3 rounded"
        style={{
          background: "linear-gradient(135deg, #312e81 0%, #6366f1 100%)",
          color: "white",
          boxShadow: "0 8px 24px rgba(99, 102, 241, 0.25)",
        }}
      >
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <FaBell size={22} />
              <h3 className="mb-0" style={{ fontWeight: 700 }}>
                System Notifications
              </h3>
            </div>
            <div style={{ opacity: 0.9, fontSize: "0.9rem" }}>
              Manage notification types and view their mailing list recipients.
            </div>
          </div>
          <Button
            color="light"
            className="d-flex align-items-center gap-2"
            onClick={fetchNotifications}
            disabled={isLoadingList}
            style={{ borderRadius: "8px", fontWeight: 600 }}
          >
            {isLoadingList ? <Spinner size="sm" /> : <FaSync />}
            Refresh
          </Button>
        </div>
      </div>

      <Row className="g-3">
        <Col lg={4}>
          <div
            className="card border-0 shadow-sm h-100"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            <div
              className="card-header bg-white border-0 py-3 px-3"
              style={{ borderBottom: "1px solid #e2e8f0" }}
            >
              <div className="fw-semibold mb-2" style={{ color: "#334155" }}>
                Notification Types
              </div>
              <div className="position-relative">
                <FaSearch
                  className="position-absolute"
                  style={{ left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
                />
                <input
                  type="text"
                  className="form-control ps-5"
                  placeholder="Search notifications..."
                  value={listSearch}
                  onChange={(e) => setListSearch(e.target.value)}
                  style={{ borderRadius: "8px", fontSize: "0.88rem" }}
                />
              </div>
            </div>
            <div className="card-body p-2" style={{ maxHeight: "68vh", overflowY: "auto" }}>
              {isLoadingList ? (
                <div className="text-center py-5">
                  <Spinner color="primary" size="sm" />
                  <div className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
                    Loading notifications...
                  </div>
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="text-center text-muted py-4" style={{ fontSize: "0.88rem" }}>
                  No notification types found.
                </div>
              ) : (
                filteredNotifications.map((item) => {
                  const isSelected =
                    selectedNotification?.system_notification_id === item.system_notification_id;
                  return (
                    <button
                      key={item.system_notification_id || item.tag}
                      type="button"
                      className="w-100 text-start border-0 mb-2"
                      onClick={() => handleSelectNotification(item)}
                      style={{
                        background: isSelected ? "#eef2ff" : "#f8fafc",
                        borderRadius: "10px",
                        padding: "0.75rem 0.85rem",
                        border: isSelected ? "2px solid #6366f1" : "1px solid #e2e8f0",
                        boxShadow: isSelected ? "0 4px 12px rgba(99,102,241,0.15)" : "none",
                        transition: "all 0.15s ease",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 700,
                              fontSize: "0.9rem",
                              color: "#1e293b",
                              lineHeight: 1.3,
                            }}
                          >
                            {formatCell(item.name)}
                          </div>
                          <div
                            className="text-muted mt-1"
                            style={{ fontSize: "0.75rem", wordBreak: "break-word" }}
                          >
                            {formatCell(item.tag)}
                          </div>
                        </div>
                        <Badge color="light" style={{ color: "#475569", flexShrink: 0 }}>
                          <FaUsers className="me-1" size={10} />
                          {formatCell(item.recipients)}
                        </Badge>
                      </div>
                      <div className="mt-2">
                        <Badge color="primary" pill style={{ fontSize: "0.68rem" }}>
                          {formatCell(item.module)}
                        </Badge>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </Col>

        <Col lg={8}>
          <div
            className="card border-0 shadow-sm h-100"
            style={{ borderRadius: "12px", overflow: "hidden" }}
          >
            {!selectedNotification ? (
              <div className="d-flex flex-column align-items-center justify-content-center text-muted py-5 px-3 h-100">
                <FaInbox size={36} className="mb-3 opacity-50" />
                <div style={{ fontWeight: 600 }}>Select a notification type</div>
                <div style={{ fontSize: "0.88rem" }} className="text-center mt-1">
                  Choose an item on the left to view settings and recipients.
                </div>
              </div>
            ) : (
              <>
                <div
                  className="px-3 py-3"
                  style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}
                >
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                    <div>
                      <h5 className="mb-1" style={{ fontWeight: 700, color: "#1e293b" }}>
                        {formatCell(selectedNotification.name)}
                      </h5>
                      <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                        {formatCell(selectedNotification.tag)}
                      </div>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Button
                        color="success"
                        size="sm"
                        className="d-flex align-items-center gap-1"
                        onClick={handleRunNotification}
                        disabled={isRunningNotification || !selectedNotification?.system_notification_id}
                        style={{ borderRadius: "8px", fontWeight: 600 }}
                      >
                        {isRunningNotification ? (
                          <>
                            <Spinner size="sm" />
                            Running...
                          </>
                        ) : (
                          <>
                            <FaPlay size={10} />
                            Run Notification
                          </>
                        )}
                      </Button>
                      <Badge color="info" pill>
                        {formatCell(selectedNotification.module)}
                      </Badge>
                    </div>
                  </div>

                  {isRunningNotification && (
                    <div
                      className="mt-3 p-3 rounded d-flex align-items-center gap-2"
                      style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}
                    >
                      <Spinner color="primary" size="sm" />
                      <span style={{ fontSize: "0.88rem", color: "#1e40af", fontWeight: 500 }}>
                        Running notification — please wait...
                      </span>
                    </div>
                  )}

                  {runNotificationResponse && !isRunningNotification && (
                    <div
                      className="mt-3 p-3 rounded"
                      style={{
                        background: runNotificationResponse.success ? "#f0fdf4" : "#fef2f2",
                        border: `1px solid ${runNotificationResponse.success ? "#bbf7d0" : "#fecaca"}`,
                      }}
                    >
                      {(runNotificationResponse.summaryLines || []).map((line, index) => (
                        <div
                          key={`${line}-${index}`}
                          style={{
                            fontSize: index === 0 ? "0.9rem" : "0.85rem",
                            fontWeight: index === 0 ? 600 : 400,
                            color: runNotificationResponse.success
                              ? index === 0
                                ? "#166534"
                                : "#15803d"
                              : "#b91c1c",
                            marginBottom: index < runNotificationResponse.summaryLines.length - 1 ? "0.35rem" : 0,
                          }}
                        >
                          {line}
                        </div>
                      ))}
                    </div>
                  )}

                  {settingsItems.length > 0 && (
                    <div className="mt-3">
                      <div
                        className="d-flex align-items-center gap-2 mb-2"
                        style={{ fontSize: "0.8rem", fontWeight: 700, color: "#475569" }}
                      >
                        <FaCog size={12} />
                        Settings
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {settingsItems.map((setting) => (
                          <span
                            key={setting.key}
                            className="badge bg-white text-dark border"
                            style={{ fontSize: "0.78rem", fontWeight: 500, padding: "0.45rem 0.6rem" }}
                          >
                            {setting.label}: <strong>{setting.value}</strong>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className="card-header bg-white border-0 py-3 px-3 d-flex flex-wrap gap-2 align-items-center justify-content-between"
                  style={{ borderBottom: "1px solid #e2e8f0" }}
                >
                  <div className="fw-semibold" style={{ color: "#334155" }}>
                    Recipients ({filteredRecipients.length})
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <Button
                      color="primary"
                      size="sm"
                      className="d-flex align-items-center gap-1"
                      onClick={openAddRecipientModal}
                      style={{ borderRadius: "8px", fontWeight: 600 }}
                    >
                      <FaPlus size={11} />
                      Add Recipient
                    </Button>
                    <div className="position-relative" style={{ minWidth: "200px", maxWidth: "280px" }}>
                      <FaSearch
                        className="position-absolute"
                        style={{ left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
                      />
                      <input
                        type="text"
                        className="form-control form-control-sm ps-5"
                        placeholder="Search recipients..."
                        value={recipientSearch}
                        onChange={(e) => setRecipientSearch(e.target.value)}
                        style={{ borderRadius: "8px" }}
                      />
                    </div>
                  </div>
                </div>

                <div className="card-body p-0">
                  {isLoadingRecipients ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" size="sm" />
                      <div className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
                        Loading recipients...
                      </div>
                    </div>
                  ) : filteredRecipients.length === 0 ? (
                    <div className="text-center text-muted py-5" style={{ fontSize: "0.88rem" }}>
                      No recipients found for this notification.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0" style={{ fontSize: "0.88rem" }}>
                        <thead style={{ background: "#f8fafc" }}>
                          <tr>
                            <th style={{ padding: "0.55rem 0.75rem", width: "48px" }}>#</th>
                            <th style={{ padding: "0.55rem 0.75rem" }}>Name</th>
                            <th style={{ padding: "0.55rem 0.75rem" }}>Email</th>
                            <th style={{ padding: "0.55rem 0.75rem" }}>Designation</th>
                            <th style={{ padding: "0.55rem 0.75rem" }}>Staff ID</th>
                            <th style={{ padding: "0.55rem 0.75rem", width: "70px" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRecipients.map((recipient, index) => (
                            <tr key={getRecipientId(recipient) || recipient.user_id || recipient.email || index}>
                              <td style={{ padding: "0.55rem 0.75rem", color: "#64748b" }}>
                                {index + 1}
                              </td>
                              <td style={{ padding: "0.55rem 0.75rem" }}>
                                {formatCell(recipient.display_name)}
                              </td>
                              <td style={{ padding: "0.55rem 0.75rem" }}>
                                {formatCell(recipient.email || recipient.user_email)}
                              </td>
                              <td style={{ padding: "0.55rem 0.75rem" }}>
                                {formatCell(recipient.designation)}
                              </td>
                              <td style={{ padding: "0.55rem 0.75rem" }}>
                                {formatCell(recipient.staff_id || recipient.staff_code)}
                              </td>
                              <td style={{ padding: "0.55rem 0.75rem" }}>
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 text-danger"
                                  title="Remove recipient"
                                  onClick={() => openDeleteRecipientModal(recipient)}
                                  disabled={!getRecipientId(recipient)}
                                >
                                  <FaTrash size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>

      <Modal isOpen={showAddRecipientModal} toggle={closeAddRecipientModal} centered>
        <ModalHeader toggle={closeAddRecipientModal}>Add Recipient</ModalHeader>
        <Form onSubmit={handleAddRecipient}>
          <ModalBody>
            <FormGroup>
              <Label>Notification</Label>
              <Input value={selectedNotification?.name || ""} readOnly disabled />
            </FormGroup>
            <FormGroup>
              <Label>Recipient Type</Label>
              <Input value="user" readOnly disabled />
            </FormGroup>
            <FormGroup>
              <Label>
                User <span className="text-danger">*</span>
              </Label>
              <Select
                isSearchable
                isClearable
                placeholder={isLoadingUsers ? "Loading users..." : "Search and select user..."}
                options={userOptions}
                value={selectedUser}
                onChange={setSelectedUser}
                isLoading={isLoadingUsers}
                isDisabled={isLoadingUsers || isAddingRecipient}
                noOptionsMessage={() => "No users found"}
                styles={{
                  control: (base) => ({ ...base, borderRadius: "8px", minHeight: "38px" }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={closeAddRecipientModal} disabled={isAddingRecipient}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={isAddingRecipient || isLoadingUsers}>
              {isAddingRecipient ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Adding...
                </>
              ) : (
                "Add Recipient"
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal isOpen={showDeleteModal} toggle={closeDeleteRecipientModal} centered>
        <ModalHeader toggle={closeDeleteRecipientModal}>Remove Recipient</ModalHeader>
        <ModalBody>
          <p className="mb-2">
            Are you sure you want to remove{" "}
            <strong>{getRecipientDisplayName(recipientToDelete)}</strong> from the mailing list for{" "}
            <strong>{selectedNotification?.name || "this notification"}</strong>?
          </p>
          <p className="text-muted mb-0" style={{ fontSize: "0.88rem" }}>
            This action cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeDeleteRecipientModal} disabled={isDeletingRecipient}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleRemoveRecipient} disabled={isDeletingRecipient}>
            {isDeletingRecipient ? (
              <>
                <Spinner size="sm" className="me-2" />
                Removing...
              </>
            ) : (
              "Remove Recipient"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SystemNotifications;
